import type { DocumentFlowDoc } from '@dealstack/shared/lib/common/business/documentFlow';
import {
	normaliseFlowFields,
	parseDocumentFlow,
} from '@dealstack/shared/lib/common/business/documentFlow';

/** A ProseMirror JSON node, loosely typed at the boundary and narrowed by the flow schema. */
export interface EditorJsonNode {
	type: string;
	attrs?: Record<string, unknown>;
	content?: EditorJsonNode[];
	marks?: { type: string; attrs?: Record<string, unknown> }[];
	text?: string;
}

/**
 * Attributes whose value carries no information, so storing it is churn.
 *
 * ProseMirror's table cell defaults both spans to 1 rather than null, and a span of 1 IS the
 * absence of a span. The importer omits them on that basis, so keeping them here would make a
 * document differ from itself depending on whether it had ever been opened in the editor.
 */
const REDUNDANT_ATTRS: Record<string, unknown> = { colspan: 1, rowspan: 1 };

/**
 * Strips what ProseMirror adds and the model does not want.
 *
 * `Node.toJSON` emits EVERY declared attribute, defaults included, so a plain paragraph comes
 * back as `attrs: { align: null, indentLeftMm: null, … }`. The flow schema declares those keys
 * optional rather than nullable, so without this the document would not validate — and a
 * stored model full of nulls would be unreadable next to a hand-written one.
 */
export const pruneEditorJson = (node: EditorJsonNode): EditorJsonNode => {
	const attrs = Object.fromEntries(
		Object.entries(node.attrs ?? {}).filter(
			([key, value]) =>
				value !== null && value !== undefined && REDUNDANT_ATTRS[key] !== value
		)
	);
	const content = (node.content ?? []).map(pruneEditorJson);
	const marks = (node.marks ?? []).map((mark) => {
		const markAttrs = Object.fromEntries(
			Object.entries(mark.attrs ?? {}).filter(
				([, value]) => value !== null && value !== undefined
			)
		);
		return {
			type: mark.type,
			...(Object.keys(markAttrs).length === 0 ? {} : { attrs: markAttrs }),
		};
	});

	return {
		type: node.type,
		...(node.text === undefined ? {} : { text: node.text }),
		...(Object.keys(attrs).length === 0 ? {} : { attrs }),
		...(marks.length === 0 ? {} : { marks }),
		...(content.length === 0 ? {} : { content }),
	};
};

/**
 * The document's body as editor content.
 *
 * Page geometry, the base font, the bindings and the conversion warnings stay OUTSIDE the
 * ProseMirror document: they describe the page the body is poured onto, not the body, and an
 * editor that could put them in an undo step could undo the page size.
 */
export const toEditorContent = (
	doc: DocumentFlowDoc
): { type: 'doc'; content: DocumentFlowDoc['content'] } => ({
	type: 'doc',
	// Normalised on the way in: a token that reached the document as TEXT — typed by hand
	// before the input rule existed — becomes a field node here, so it shows as a chip and is
	// offered for linking rather than sitting there reported as an unusable name.
	content: normaliseFlowFields(doc).content,
});

/**
 * A table as the flow model states one, whatever the editor handed over.
 *
 * Two things a PASTED table has that an imported one never does. TipTap parses `<th>` into its
 * own `tableHeader` node, which the flow model has no equivalent for — it puts the header flag
 * on the ROW — and a pasted table states no column widths, which the model requires. Both make
 * the document unrepresentable, and the cost of that is in `FlowDocumentEditor`: the throw used
 * to escape `onUpdate` and stop every later edit being reported, so a save wrote the document as
 * it was BEFORE the paste and discarded everything typed after it.
 *
 * Widths are shared equally across the printable width, which is what the importer does for a
 * table whose `.docx` states no grid.
 */
const tableFromEditor = (node: EditorJsonNode, printableWidthMm: number): EditorJsonNode => {
	const rows = (node.content ?? []).map((row) => {
		const cells = row.content ?? [];
		const isHeader = cells.length > 0 && cells.every((cell) => cell.type === 'tableHeader');
		return {
			...row,
			...(isHeader ? { attrs: { ...(row.attrs ?? {}), isHeader: true } } : {}),
			content: cells.map((cell) => ({ ...cell, type: 'tableCell' })),
		};
	});
	const columns = Math.max(1, ...rows.map((row) => (row.content ?? []).length));
	const stated = node.attrs?.['columnWidthsMm'];
	const widths =
		Array.isArray(stated) && stated.length > 0
			? stated
			: Array.from({ length: columns }, () => printableWidthMm / columns);

	return { ...node, attrs: { ...(node.attrs ?? {}), columnWidthsMm: widths }, content: rows };
};

const fromEditorNode = (node: EditorJsonNode, printableWidthMm: number): EditorJsonNode => {
	const mapped = node.type === 'table' ? tableFromEditor(node, printableWidthMm) : node;
	return {
		...mapped,
		...(mapped.content === undefined
			? {}
			: { content: mapped.content.map((child) => fromEditorNode(child, printableWidthMm)) }),
	};
};

/** Editor content folded back into the document it came from, validated on the way. */
export const fromEditorContent = (
	content: EditorJsonNode,
	source: DocumentFlowDoc
): DocumentFlowDoc => {
	const { page } = source.attrs;
	const printableWidthMm = Math.max(1, page.widthMm - page.marginLeftMm - page.marginRightMm);
	return parseDocumentFlow({
		...source,
		content: (pruneEditorJson(content).content ?? []).map((node) =>
			fromEditorNode(node, printableWidthMm)
		),
	});
};
